/**
 * Launcher view model — binds the panel's existing state to the idle surface.
 *
 * The panel already resolves everything the launcher needs; this assembles it
 * and owns the one new side effect (recording a visit), so the panel gains a
 * single prop rather than eight.
 *
 * @module useBrowserLauncher
 */
import { useEffect, useMemo } from "react";

import {
  browserHistoryScopeKey,
  selectBrowserHistoryEntries,
  useBrowserHistoryStore,
} from "./browserHistoryStore";
import {
  resolveBrowserLauncherSections,
  type BrowserLauncherSections,
} from "./browserLauncherLogic";

export interface BrowserLauncherLaunchAction {
  readonly label: string;
  readonly detail: string;
  readonly actionLabel: string;
  readonly busy: boolean;
  readonly onLaunch: () => void;
}

export interface BrowserLauncherViewModel {
  readonly sections: BrowserLauncherSections;
  readonly onOpenUrl: (url: string) => void;
  readonly onRemoveRecent: (url: string) => void;
  readonly launchAction: BrowserLauncherLaunchAction | undefined;
}

export interface UseBrowserLauncherInput {
  readonly environmentId: string;
  readonly projectId: string | null | undefined;
  readonly threadId: string;
  readonly devServerPreviewUrl: string | null;
  readonly devRuntimePreviewUrl: string | null;
  readonly onOpenUrl: (url: string) => void;
  readonly launchAction: BrowserLauncherLaunchAction | undefined;
}

export function useBrowserLauncher(input: UseBrowserLauncherInput): BrowserLauncherViewModel {
  const scope = browserHistoryScopeKey({
    environmentId: input.environmentId,
    projectId: input.projectId,
    threadId: input.threadId,
  });
  const recents = useBrowserHistoryStore((state) => selectBrowserHistoryEntries(state, scope));
  const removeEntry = useBrowserHistoryStore((state) => state.removeEntry);

  const sections = useMemo(
    () =>
      resolveBrowserLauncherSections({
        devServerPreviewUrl: input.devServerPreviewUrl,
        devRuntimePreviewUrl: input.devRuntimePreviewUrl,
        recents,
      }),
    [input.devRuntimePreviewUrl, input.devServerPreviewUrl, recents],
  );

  return {
    sections,
    onOpenUrl: input.onOpenUrl,
    onRemoveRecent: (url: string) => removeEntry({ scope, url }),
    launchAction: input.launchAction,
  };
}

/**
 * Records the page the panel is actually showing.
 *
 * Keyed on the settled URL rather than on every snapshot: a tab emits many
 * updates per navigation (title arriving after the URL, capture ids, viewport
 * changes), and each one would otherwise rewrite the same history row and churn
 * a persisted write. `normalizeHistoryUrl` drops `about:blank` and non-http
 * schemes, so the parked pre-navigation state never lands in history.
 */
export function useRecordBrowserVisit(input: {
  readonly environmentId: string;
  readonly projectId: string | null | undefined;
  readonly threadId: string;
  readonly url: string | null;
  readonly title: string | null;
  readonly navigationStatus: string | null;
}): void {
  const scope = browserHistoryScopeKey({
    environmentId: input.environmentId,
    projectId: input.projectId,
    threadId: input.threadId,
  });
  const url = input.url;
  const title = input.title;
  const failed = input.navigationStatus === "failed";

  useEffect(() => {
    if (url === null || failed) {
      return;
    }
    useBrowserHistoryStore.getState().recordVisit({
      scope,
      url,
      ...(title === null ? {} : { title }),
      visitedAt: Date.now(),
    });
  }, [failed, scope, title, url]);
}
